/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, September 7th 2022, 2:49:25 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ArticleIcon from "@mui/icons-material/Article";
import ViewListIcon from "@mui/icons-material/ViewList";
import EmailIcon from "@mui/icons-material/Email";
import GridViewIcon from "@mui/icons-material/GridView";
import { LAYOUT_OPTIONS } from "lib/schemas";

interface ILayoutTypeData {
  layoutType: keyof typeof LAYOUT_OPTIONS;
  layoutTypeTitle: string;
  layoutTypeIcon?: React.ReactNode;
  layoutTypeColor: React.ComponentProps<typeof IconButton>["color"];
}

interface LayoutTogglerProps {
  selectedLayoutType: keyof typeof LAYOUT_OPTIONS;
  toggleLayoutType: (layoutType: keyof typeof LAYOUT_OPTIONS) => void;
  layoutTypeData: ILayoutTypeData[];
}

const defaultIcons: Record<keyof typeof LAYOUT_OPTIONS, React.ReactNode> = {
  document: <TextSnippetIcon />,
  qr_code: <QrCode2Icon />,
  detail_display: <ArticleIcon />,
  list_display: <ViewListIcon />,
  email: <EmailIcon />,
  general: <GridViewIcon />,
};

export const LayoutToggler = ({ selectedLayoutType, toggleLayoutType, layoutTypeData }: LayoutTogglerProps) => {
  const getIconButtonStyles = (layoutType: keyof typeof LAYOUT_OPTIONS) => {
    return selectedLayoutType === layoutType ? { borderRadius: 1, border: "1px solid" } : { borderRadius: 1 };
  };

  return (
    <Stack direction="row" spacing={1}>
      {layoutTypeData.map(({ layoutType, layoutTypeTitle, layoutTypeIcon, layoutTypeColor }) => (
        <Tooltip key={layoutType} title={<Typography>{layoutTypeTitle}</Typography>} placement="top" arrow>
          <IconButton
            sx={getIconButtonStyles(layoutType)}
            onClick={() => toggleLayoutType(layoutType)}
            color={layoutTypeColor}
          >
            {layoutTypeIcon ?? defaultIcons[layoutType]}
          </IconButton>
        </Tooltip>
      ))}
    </Stack>
  );
};
